import type { AnchorHTMLAttributes, ReactNode } from 'react';

import { cn } from '@/shared/lib';

/**
 * Een download is een link, geen knop: rechtermuisknop, nieuw tabblad en
 * "opslaan als" horen te werken.
 *
 * TODO(agent: design-system): geef `Button` een `as`-prop, dan kan dit weg.
 * Tot die tijd staan hier dezelfde tokens als in `Button` - geen hexcodes.
 */

export type ActionLinkVariant = 'primary' | 'outline' | 'dark';

export type ActionLinkSize = 'md' | 'xl';

const variantStyles: Record<ActionLinkVariant, string> = {
    primary:
        'border-0 bg-[var(--accent)] text-[var(--text-on-dark)] font-medium hover:bg-[var(--accent-hover)]',
    outline:
        'border border-[var(--border-control)] bg-[var(--surface-card)] text-[var(--text-primary)] hover:border-[var(--accent)] hover:text-[var(--accent)]',
    dark: 'border-0 bg-[var(--ink-800)] text-[var(--text-on-dark)] font-medium hover:bg-[var(--ink-600)]',
};

const sizeStyles: Record<ActionLinkSize, string> = {
    md: 'min-h-[44px] px-4 py-[11px] text-[13px] rounded-[7px]',
    xl: 'min-h-[48px] px-5 py-[14px] text-[14px] rounded-[8px]',
};

export type ActionLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
    variant?: ActionLinkVariant;
    size?: ActionLinkSize;
    children: ReactNode;
};

export function ActionLink({
    variant = 'outline',
    size = 'md',
    className,
    children,
    ...rest
}: ActionLinkProps) {
    return (
        <a
            className={cn(
                'inline-flex items-center justify-center gap-2 text-center',
                'font-[family-name:var(--font-body)] leading-[1.25]',
                'cursor-pointer transition-colors duration-100',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]',
                sizeStyles[size],
                variantStyles[variant],
                className,
            )}
            {...rest}
        >
            {children}
        </a>
    );
}
